import React, { useCallback, useMemo } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useEventDetailsGroupSales(context) {
  const {
    API_URL,
    CHAT_GROUP_EXECUTE_MODE,
    CHAT_GROUP_MODE,
    EventListRow,
    FLYER_IMAGE_PATH,
    appendVariant,
    assignedAdmins,
    assignmentCandidates,
    assignmentDraftIds,
    assignmentSaving,
    assignmentSearch,
    canEditCastingSettings,
    canEditEventInfo,
    canEditParticipants,
    canUseEventChat,
    chatGroupConfirmState,
    chatGroupDraftMode,
    chatGroupSaving,
    chatGroupSetting,
    eventData,
    eventId,
    eventReadOnlyTitle,
    fetchChatGroupSetting,
    fetchEventAssignmentAdminCandidates,
    fetchGroupEvents,
    formatDateTimeJST,
    genres,
    groupEvents,
    groupFlyerLandscapeMap,
    groupRootEventUuid,
    hasGroupEvents,
    isTodayDate,
    loggedInAdminId,
    navigate,
    notificationSilenceLoading,
    notificationSilenceWindows,
    parsePgIntArray,
    previewEventChatGroupSetting,
    refreshEventChatUnread,
    setAssignmentCandidates,
    setAssignmentCandidatesLoading,
    setAssignmentDraftIds,
    setAssignmentSaving,
    setAssignmentSettingsOpen,
    setChatGroupConfirmState,
    setChatGroupPreviewLoading,
    setChatGroupSaving,
    setEventData,
    setGroupEvents,
    setGroupFlyerLandscapeMap,
    ticketSummary,
    tickets,
    toUrl,
    toastError,
    toastSuccess,
    updateEventAssignments,
    updateEventChatGroupSetting,
    useEventMembersVisibilityControl,
  } = context;
    const resolveGroupGenreObjs = useCallback(
      (maybeGenre) => {
        const genresById = new Map();
        const genresByValueLower = new Map();
        for (const g of genres || []) {
          genresById.set(Number(g.id), g);
          if (g?.value) genresByValueLower.set(String(g.value).toLowerCase(), g);
        }
        const result = [];
        const pushUnique = (genreObj) => {
          if (!genreObj) return;
          const id = Number(genreObj.id);
          if (!result.some((row) => Number(row.id) === id)) result.push(genreObj);
        };
        const handleEl = (el) => {
          if (Number.isInteger(el) || /^\d+$/.test(String(el))) {
            pushUnique(genresById.get(Number(el)));
          } else if (typeof el === 'string') {
            pushUnique(genresByValueLower.get(el.toLowerCase()));
          }
        };
        if (Array.isArray(maybeGenre)) {
          maybeGenre.forEach(handleEl);
        } else if (typeof maybeGenre === 'string') {
          const t = maybeGenre.trim();
          if (t.startsWith('{')) {
            (parsePgIntArray(t) || []).forEach((id) => pushUnique(genresById.get(Number(id))));
          } else if (t.startsWith('[')) {
            try {
              const arr = JSON.parse(t);
              if (Array.isArray(arr)) arr.forEach(handleEl);
            } catch {}
          } else if (/^\d+$/.test(t)) {
            pushUnique(genresById.get(Number(t)));
          } else {
            pushUnique(genresByValueLower.get(t.toLowerCase()));
          }
        } else if (typeof maybeGenre === 'number') {
          pushUnique(genresById.get(Number(maybeGenre)));
        }
        return result;
      },
      [genres]
    );
    const renderGroupGenreBadges = useCallback(
      (event) => {
        const genreList = resolveGroupGenreObjs(event?.genre);
        const hasGenres = Array.isArray(genreList) && genreList.length > 0;
        const isConciergeEvent = Boolean(event?.is_concierge_event || event?.concierge_application_id);
        const hasCustomBadges = Boolean(event?.is_group_root || event?.is_current_event);
        if (!hasGenres && !isConciergeEvent && !hasCustomBadges) return null;
        return (
          <div className="event-list-row__badges" aria-label="イベントジャンル">
            {event?.is_group_root ? (
              <span className="genre event-list-row__badge group-role-root">グループ親</span>
            ) : null}
            {event?.is_current_event ? (
              <span className="genre event-list-row__badge group-role-current">表示中</span>
            ) : null}
            {isConciergeEvent ? (
              <span className="genre event-list-row__badge" style={{ backgroundColor: 'var(--purple-600)', color: '#fff' }}>
                コンシェルジュ
              </span>
            ) : null}
            {hasGenres
              ? genreList.map((genre, index) => {
                  const key =
                    genre?.id != null
                      ? `group-genre-${event?.event_uuid}-${genre.id}`
                      : `group-genre-${event?.event_uuid}-${genre?.value || index}`;
                  return (
                    <span
                      key={key}
                      className="genre event-list-row__badge"
                      style={{
                        backgroundColor: genre?.color || 'var(--indigo-500)',
                        color: genre?.text_color || '#fff',
                      }}
                    >
                      {genre?.label || genre?.name || genre?.value || 'ジャンル'}
                    </span>
                  );
                })
              : null}
          </div>
        );
      },
      [resolveGroupGenreObjs]
    );
    const buildGroupFlyerSrc = useCallback(
      (event) => {
        if (!event?.flyer_front_url) return null;
        return appendVariant(toUrl(FLYER_IMAGE_PATH, event.flyer_front_url), 'card');
      },
      [toUrl]
    );
    const resolveGroupVenueDisplay = useCallback((event) => {
      const venueId = event?.venue_id ?? null;
      const venueName = event?.venue || '';
      return {
        name: venueName || '',
        link: venueId ? `/admin/workspaces/event-operations/venues/${venueId}` : null,
      };
    }, []);
    const handleGroupFlyerLoad = useCallback((eventUuid, target) => {
      if (!eventUuid || !target) return;
      const { naturalWidth, naturalHeight } = target;
      if (!naturalWidth || !naturalHeight) return;
      const isLandscape = naturalWidth > naturalHeight;
      setGroupFlyerLandscapeMap((prev) => {
        if (prev[eventUuid] === isLandscape) return prev;
        return { ...prev, [eventUuid]: isLandscape };
      });
    }, []);
    const groupEventRows = useMemo(
      () =>
        (Array.isArray(groupEvents) ? groupEvents : []).filter(
          (event) => event && event.event_uuid
        ),
      [groupEvents]
    );
    const groupRootEvent = useMemo(
      () =>
        groupEventRows.find((event) => event.event_uuid === groupRootEventUuid) ||
        groupEventRows.find((event) => event.is_group_root) ||
        null,
      [groupEventRows, groupRootEventUuid]
    );
    const groupRootPerformanceLabel = useMemo(() => {
      const raw = String(groupRootEvent?.performance_type || '').trim();
      return raw || '公演名未設定';
    }, [groupRootEvent?.performance_type]);
    const shouldShowGroupEventsPanel = hasGroupEvents && groupEventRows.length > 0;
    const {
      membersVisibleToArtists,
      membersVisibilitySaving,
      membersVisibilityButtonLabel,
      membersVisibilityButtonClass,
      membersVisibilityConfirmState,
      closeMembersVisibilityConfirmModal,
      handleMembersVisibilityConfirmSingle,
      handleMembersVisibilityConfirmGroupAll,
      handleMembersVisibilityToggle,
    } = useEventMembersVisibilityControl({
      apiUrl: API_URL,
      eventId,
      currentEvent: eventData,
      groupEventRows,
      hasGroupEvents,
      canEditParticipants,
      readOnlyTitle: eventReadOnlyTitle,
      setCurrentEventState: setEventData,
      setGroupEventsState: setGroupEvents,
    });
    const currentEventUuid = eventData?.event_uuid || eventId || null;
    const notificationSilenceEnabledCount = useMemo(
      () => notificationSilenceWindows.filter((item) => item?.enabled !== false).length,
      [notificationSilenceWindows]
    );
    const notificationSilenceSummary = useMemo(() => {
      if (notificationSilenceLoading) return '読み込み中';
      if (!notificationSilenceEnabledCount) return '未設定';
      return `${notificationSilenceEnabledCount}件設定中`;
    }, [notificationSilenceEnabledCount, notificationSilenceLoading]);
    const isCurrentEventGroupRoot = Boolean(
      currentEventUuid &&
        groupRootEventUuid &&
        String(currentEventUuid) === String(groupRootEventUuid)
    );
    const chatGroupCurrentMode =
      chatGroupSetting?.chat_mode === CHAT_GROUP_MODE.GROUP_UNIFIED
        ? CHAT_GROUP_MODE.GROUP_UNIFIED
        : CHAT_GROUP_MODE.SEPARATE;
    const chatGroupDraftChanged = chatGroupDraftMode !== chatGroupCurrentMode;
    const chatGroupTargetExecuteMode =
      chatGroupDraftMode === CHAT_GROUP_MODE.GROUP_UNIFIED
        ? CHAT_GROUP_EXECUTE_MODE.MERGE
        : CHAT_GROUP_EXECUTE_MODE.SPLIT;
    const canEditChatGroupSetting =
      canUseEventChat && shouldShowGroupEventsPanel && isCurrentEventGroupRoot && canEditEventInfo;
    const chatGroupUpdatedAtLabel = useMemo(
      () =>
        chatGroupSetting?.updated_at
          ? formatDateTimeJST(chatGroupSetting.updated_at)
          : '',
      [chatGroupSetting?.updated_at]
    );
    const closeChatGroupConfirmModal = useCallback(() => {
      if (chatGroupSaving) return;
      setChatGroupConfirmState({
        open: false,
        executeMode: null,
        targetMode: chatGroupCurrentMode,
        preview: null,
      });
    }, [chatGroupCurrentMode, chatGroupSaving]);
    const openChatGroupConfirmModal = useCallback(async () => {
      if (!canEditChatGroupSetting) return;
      if (!chatGroupDraftChanged) return;
      if (!eventId) return;

      setChatGroupPreviewLoading(true);
      try {
        const data = await previewEventChatGroupSetting(eventId, { execute_mode: chatGroupTargetExecuteMode });
        setChatGroupConfirmState({
          open: true,
          executeMode: chatGroupTargetExecuteMode,
          targetMode: chatGroupDraftMode,
          preview: data?.preview || null,
        });
      } catch (err) {
        toastError(err?.message || 'グループチャット変更プレビューの取得に失敗しました');
      } finally {
        setChatGroupPreviewLoading(false);
      }
    }, [
      canEditChatGroupSetting,
      chatGroupDraftChanged,
      eventId,
      chatGroupTargetExecuteMode,
      chatGroupDraftMode,
      toastError,
    ]);
    const submitChatGroupModeChange = useCallback(async () => {
      if (!canUseEventChat || !chatGroupConfirmState.open || !eventId) return;
      setChatGroupSaving(true);
      try {
        await updateEventChatGroupSetting(eventId, {
          chat_mode: chatGroupConfirmState.targetMode,
          execute_mode: chatGroupConfirmState.executeMode,
        });
        toastSuccess(
          chatGroupConfirmState.executeMode === CHAT_GROUP_EXECUTE_MODE.MERGE
            ? 'グループイベントのチャットを統合しました。'
            : 'グループイベントのチャットを分離しました。'
        );
        setChatGroupConfirmState({
          open: false,
          executeMode: null,
          targetMode: chatGroupConfirmState.targetMode,
          preview: null,
        });
        await Promise.all([fetchChatGroupSetting(), fetchGroupEvents()]);
        refreshEventChatUnread();
      } catch (err) {
        toastError(err?.message || 'グループチャット設定の更新に失敗しました');
      } finally {
        setChatGroupSaving(false);
      }
    }, [
      canUseEventChat,
      chatGroupConfirmState.executeMode,
      chatGroupConfirmState.open,
      chatGroupConfirmState.targetMode,
      eventId,
      fetchChatGroupSetting,
      fetchGroupEvents,
      refreshEventChatUnread,
      toastError,
      toastSuccess,
    ]);
    const renderGroupEventRow = useCallback(
      (event) => {
        const venueDisplay = resolveGroupVenueDisplay(event);
        const venueForRow =
          venueDisplay.name || venueDisplay.link
            ? { ...venueDisplay, name: venueDisplay.name || '会場未設定' }
            : null;
        return (
          <EventListRow
            key={event.event_uuid}
            event={event}
            imgSrc={buildGroupFlyerSrc(event)}
            imgAlt={event.name ? `${event.name} のフライヤー` : 'フライヤー'}
            isLandscape={!!groupFlyerLandscapeMap[event.event_uuid]}
            onFlyerLoad={(img) => handleGroupFlyerLoad(event.event_uuid, img)}
            onClick={() => navigate(`/admin/workspaces/event-operations/events/${event.event_uuid}`)}
            isToday={isTodayDate(event.event_date)}
            todayLabel="本日"
            isCancelled={!!event?.canceled_at}
            venueDisplay={venueForRow}
            badges={renderGroupGenreBadges(event)}
            progressItems={[]}
          />
        );
      },
      [
        buildGroupFlyerSrc,
        groupFlyerLandscapeMap,
        handleGroupFlyerLoad,
        isTodayDate,
        navigate,
        renderGroupGenreBadges,
        resolveGroupVenueDisplay,
      ]
    );
    const vendors = useMemo(
      () => (Array.isArray(eventData?.vendors) ? eventData.vendors : []),
      [eventData]
    );
    const assignmentButtonLabel = '担当者設定';
    const assignmentButtonDisabled = !canEditCastingSettings || assignmentSaving || !loggedInAdminId;
    const filteredAssignmentCandidates = useMemo(() => {
      const keyword = assignmentSearch.trim().toLowerCase();
      if (!keyword) return assignmentCandidates;
      return assignmentCandidates.filter((admin) => {
        const haystack = [
          admin?.display_name,
          admin?.full_name,
          admin?.nickname,
          admin?.username,
          admin?.position,
          admin?.department,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(keyword);
      });
    }, [assignmentCandidates, assignmentSearch]);

    const openAssignmentSettings = async () => {
      if (!eventId || !loggedInAdminId || assignmentSaving) return;
      if (!canEditCastingSettings) {
        toastError(eventReadOnlyTitle);
        return;
      }
      setAssignmentSettingsOpen(true);
      setAssignmentDraftIds(
        assignedAdmins
          .map((admin) => Number(admin?.admin_user_id ?? admin?.id))
          .filter((id) => Number.isInteger(id) && id > 0)
      );
      if (assignmentCandidates.length) return;
      setAssignmentCandidatesLoading(true);
      try {
        const data = await fetchEventAssignmentAdminCandidates();
        setAssignmentCandidates(Array.isArray(data?.adminUsers) ? data.adminUsers : []);
      } catch (error) {
        console.error('fetch assignment candidates error:', error);
        toastError(error?.message || '担当者候補の取得に失敗しました');
      } finally {
        setAssignmentCandidatesLoading(false);
      }
    };

    const toggleAssignmentDraftId = (adminId) => {
      const id = Number(adminId);
      if (!Number.isInteger(id) || id <= 0) return;
      setAssignmentDraftIds((prev) =>
        prev.some((value) => Number(value) === id)
          ? prev.filter((value) => Number(value) !== id)
          : [...prev, id]
      );
    };

    const handleAssignmentSettingsSave = async () => {
      if (!eventId || assignmentSaving) return;
      setAssignmentSaving(true);
      try {
        const data = await updateEventAssignments(eventId, assignmentDraftIds);
        const nextAssignments = Array.isArray(data?.assignments) ? data.assignments : [];
        setEventData((prev) => (prev ? { ...prev, assigned_admins: nextAssignments } : prev));
        setAssignmentSettingsOpen(false);
        toastSuccess('担当者を更新しました');
      } catch (error) {
        console.error('assignment settings save error:', error);
        toastError(error?.message || '担当者の更新に失敗しました');
      } finally {
        setAssignmentSaving(false);
      }
    };

    // ── データ未取得時の描画 ──────────────────────────────────────────────
    const progressPct = Math.min(100, Math.max(0, Math.round(ticketSummary?.progress_pct || 0)));
    const soldTotal = ticketSummary?.sold_total ?? 0;
    const totalCapacity = ticketSummary?.total_capacity ?? 0;
    const grossRevenue = ticketSummary?.gross_revenue_total ?? 0;
    const netRevenue = ticketSummary?.net_revenue_total ?? 0;
    const channelShares = useMemo(() => {
      const channelColors = [
        'var(--green-500)',
        'var(--sky-500)',
        'var(--purple-500)',
        'var(--orange-500)',
        'var(--red-600)',
        'var(--cyan-600)',
      ];
      const list = Array.isArray(ticketSummary?.by_channel) ? ticketSummary.by_channel : [];
      const totalSold = list.reduce((s, c) => s + (Number(c.sold_total) || 0), 0);
      if (!totalSold) return [];
      return list
        .map((c, idx) => {
          const sold = Number(c.sold_total) || 0;
          const pct = sold > 0 ? (sold / totalSold) * 100 : 0;
          return {
            ...c,
            sold,
            percent: Math.round(pct * 10) / 10,
            color: channelColors[idx % channelColors.length],
          };
        })
        .filter((c) => c.sold > 0)
        .sort((a, b) => b.sold - a.sold);
    }, [ticketSummary]);

    // チケット種別ごとの販売率（quantity を 100% とした進捗）
    const ticketCapacityProgress = useMemo(() => {
      if (!ticketSummary || !Array.isArray(tickets)) return [];
      const byId = new Map(
        (Array.isArray(ticketSummary.by_ticket_type) ? ticketSummary.by_ticket_type : []).map(
          (row) => [String(row.ticket_type_id || ''), row]
        )
      );
      return tickets
        .map((t, idx) => {
          const cap = Number(t.quantity) || 0;
          if (cap <= 0) {
            return null;
          }
          const agg = byId.get(String(t.id)) || null;
          const sold = agg ? Number(agg.sold_total) || 0 : 0;
          const pctRaw = cap > 0 ? (sold / cap) * 100 : null;
          const pct =
            pctRaw == null || !Number.isFinite(pctRaw) ? null : Math.round(pctRaw * 10) / 10;
          return {
            id: t.id ?? idx,
            label: t.ticket_type || t.name || `種別${idx + 1}`,
            capacity: cap,
            sold,
            pct,
          };
        })
        .filter(Boolean);
    }, [ticketSummary, tickets]);

    // YYYY-MM-DD をローカル日付で解釈するヘルパー
    const parseLocalYmd = (value) => {
      if (!value) return new Date('invalid');
      const str = String(value).slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [y, m, d] = str.split('-').map((v) => Number(v));
        return new Date(y, m - 1, d);
      }
      const d = new Date(str);
      return Number.isNaN(d.getTime()) ? new Date('invalid') : d;
    };

    const dailySeries = useMemo(() => {
      const arr = Array.isArray(ticketSummary?.daily) ? ticketSummary.daily : [];
      const base = arr
        .filter((d) => d.sale_date)
        .map((d) => ({
          date: parseLocalYmd(d.sale_date),
          sold: Number(d.sold_total) || 0,
          net: Number(d.net_revenue) || 0,
        }))
        .filter((d) => !Number.isNaN(d.date.getTime()))
        .sort((a, b) => a.date - b.date);

      if (!base.length) return [];

      // 最初の販売日〜最後の販売日を連続する日付で埋める（売上0の日も含める）
      const filled = [];
      const first = new Date(base[0].date);
      first.setHours(0, 0, 0, 0);
      const last = new Date(base[base.length - 1].date);
      last.setHours(0, 0, 0, 0);

      let cursor = new Date(first);
      let i = 0;
      while (cursor.getTime() <= last.getTime()) {
        const t = cursor.getTime();
        let sold = 0;
        let net = 0;
        if (i < base.length) {
          const curT = base[i].date.getTime();
          if (curT === t) {
            sold = base[i].sold;
            net = base[i].net;
            i += 1;
          }
        }
        filled.push({
          date: new Date(cursor),
          sold,
          net,
        });
        cursor.setDate(cursor.getDate() + 1);
      }

      return filled;
    }, [ticketSummary]);

    const recentStats = useMemo(() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      const lastWeekStart = new Date(weekStart);
      lastWeekStart.setDate(lastWeekStart.getDate() - 7);
      const lastWeekEnd = new Date(weekStart);
      lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);

      const sumRange = (start, end) => {
        const startMs = start.getTime();
        const endMs = end.getTime();
        return dailySeries.reduce(
          (acc, r) => {
            const t = r.date.getTime();
            if (t >= startMs && t <= endMs) {
              acc.sold += r.sold;
              acc.net += r.net;
            }
            return acc;
          },
          { sold: 0, net: 0 }
        );
      };

      const yesterdayStats = sumRange(yesterday, yesterday);
      const thisWeekStats = sumRange(weekStart, today);
      const lastWeekStats = sumRange(lastWeekStart, lastWeekEnd);

      return {
        yesterday: yesterdayStats,
        thisWeek: thisWeekStats,
        lastWeek: lastWeekStats,
      };
    }, [dailySeries]);

    const linePoints = useMemo(() => {
      if (!dailySeries.length) return [];

      // 日別の販売数を累計に変換（加算式）
      const basePoints = [];
      let acc = 0;
      dailySeries.forEach((r) => {
        acc += r.sold;
        basePoints.push({
          label: `${String(r.date.getMonth() + 1).padStart(2, '0')}/${String(
            r.date.getDate()
          ).padStart(2, '0')}`,
          date: r.date,
          value: acc,
        });
      });

      const maxPoints = 60;
      const manyPoints = basePoints.length;
      let step = 1;
      if (manyPoints > maxPoints) {
        step = manyPoints > 120 ? 7 : 3;
      }
      if (step === 1) return basePoints;

      const aggregated = [];
      let idx = 0;
      while (idx < basePoints.length) {
        const slice = basePoints.slice(idx, idx + step);
        const last = slice[slice.length - 1];
        const label = `${slice[0].label}${slice.length > 1 ? `- ${slice[slice.length - 1].label}` : ''}`;
        aggregated.push({ label, value: last.value, date: last.date });
        idx += step;
      }
      return aggregated;
    }, [dailySeries]);
  return {
    resolveGroupGenreObjs,
    renderGroupGenreBadges,
    buildGroupFlyerSrc,
    resolveGroupVenueDisplay,
    handleGroupFlyerLoad,
    groupEventRows,
    groupRootEvent,
    groupRootPerformanceLabel,
    shouldShowGroupEventsPanel,
    membersVisibleToArtists,
    membersVisibilitySaving,
    membersVisibilityButtonLabel,
    membersVisibilityButtonClass,
    membersVisibilityConfirmState,
    closeMembersVisibilityConfirmModal,
    handleMembersVisibilityConfirmSingle,
    handleMembersVisibilityConfirmGroupAll,
    handleMembersVisibilityToggle,
    currentEventUuid,
    notificationSilenceEnabledCount,
    notificationSilenceSummary,
    isCurrentEventGroupRoot,
    chatGroupCurrentMode,
    chatGroupDraftChanged,
    chatGroupTargetExecuteMode,
    canEditChatGroupSetting,
    chatGroupUpdatedAtLabel,
    closeChatGroupConfirmModal,
    openChatGroupConfirmModal,
    submitChatGroupModeChange,
    renderGroupEventRow,
    vendors,
    assignmentButtonLabel,
    assignmentButtonDisabled,
    filteredAssignmentCandidates,
    openAssignmentSettings,
    toggleAssignmentDraftId,
    handleAssignmentSettingsSave,
    progressPct,
    soldTotal,
    totalCapacity,
    grossRevenue,
    netRevenue,
    channelShares,
    ticketCapacityProgress,
    dailySeries,
    recentStats,
    linePoints,
  };
}
